// src/pages/PagesEnseignants/ObjectifsAdmin/ObjectifsAdmin.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { FaFilter, FaPlus, FaSearch, FaTrash, FaEdit, FaCheck } from "react-icons/fa";

import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import { getGroupsList } from "../../../services/groupServices";
import { getSettings } from "../../../services/settingsServices";
import { getAllAdherentsListWithGroup } from "../../../services/adherentServices";

import {
  getObjectifsByAdherent,
  createObjectif,
  updateObjectif,
  deleteObjectif,
  setObjectifDone,
  setObjectifProgress,
} from "../../../services/objectifsServices";

import CreateEditObjectifModal from "./../../PagesUtilisateurs/DashboardUsers/CreateEditObjectifModal";

/* ---------- Helpers ---------- */
const TYPES = ["loisir","ceinture","poids","renforcement","competition","kata","uv2","uv3_points","autre"];

const seasonNeighbors = (active) => {
  const [a, b] = String(active || "").split("-").map(Number);
  if (!a || !b) return { prev: "", next: "" };
  return { prev: `${a - 1}-${b - 1}`, next: `${a + 1}-${b + 1}` };
};

const seasonLabel = (d = new Date()) => {
  const y = d.getFullYear();
  const m = d.getMonth();
  const start = m >= 8 ? y : y - 1;
  return `${start}-${start + 1}`;
};

const inGroup = (adherent, groupName) =>
  !groupName || (adherent?.Groups || []).some((g) => g.nom === groupName);

/* ---------- UI atoms ---------- */
function Pill({ children, tone = "violet" }) {
  const map = {
    violet: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200",
    slate: "bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200",
    rose: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200",
  };
  return <span className={`inline-flex items-center px-2 py-[2px] rounded-full text-xs font-medium ${map[tone] || map.violet}`}>{children}</span>;
}

function Progress({ value = 0 }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="w-full">
      <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
        <div className="h-2 rounded-full bg-gradient-to-r from-emerald-500 via-violet-500 to-orange-400 transition-all" style={{ width: `${v}%` }} />
      </div>
      <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">{v}%</div>
    </div>
  );
}

/* ---------- Filters bar ---------- */
function FiltersBar({
  loading,
  groups, filterGroup, onGroupChange,
  seasons, selectedSeason, onSeasonChange,
  typeFilter, onTypeChange,
  isDoneFilter, onIsDoneChange,
  search, onSearchChange,
}) {
  return (
    <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur p-3 sm:p-4">
      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200 mb-2">
        <FaFilter /><span className="font-semibold">Filtres</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <select
          value={filterGroup}
          onChange={(e) => onGroupChange(e.target.value)}
          disabled={loading}
          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
        >
          <option value="">Tous les groupes</option>
          {(groups || []).map((g) => <option key={g.id} value={g.nom}>{g.nom}</option>)}
        </select>

        <select
          value={selectedSeason}
          onChange={(e) => onSeasonChange(e.target.value)}
          disabled={loading}
          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
        >
          {seasons?.length
            ? seasons.map((s) => <option key={s} value={s}>{s}</option>)
            : <option value={seasonLabel()}>{seasonLabel()}</option>}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => onTypeChange(e.target.value)}
          disabled={loading}
          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
        >
          <option value="">Type (tous)</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <select
          value={isDoneFilter}
          onChange={(e) => onIsDoneChange(e.target.value)}
          disabled={loading}
          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
        >
          <option value="">Statut (tous)</option>
          <option value="true">Terminés</option>
          <option value="false">En cours</option>
        </select>

        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60"><FaSearch /></span>
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Rechercher…"
            disabled={loading}
            className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 pl-9 pr-3 py-2"
          />
        </div>
      </div>
    </div>
  );
}

/* ---------- Row actions / Table ---------- */
function RowActions({ row, onEdit, onDelete, onToggleDone }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onToggleDone(row)}
        className={`px-2 py-1 rounded-lg text-white ${row.is_done ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-500 hover:bg-slate-600"}`}
        title={row.is_done ? "Marquer non terminé" : "Marquer terminé"}
      >
        <FaCheck />
      </button>
      <button
        onClick={() => onEdit(row)}
        className="px-2 py-1 rounded-lg ring-1 ring-slate-300 dark:ring-slate-700"
        title="Modifier"
      >
        <FaEdit />
      </button>
      <button
        onClick={() => onDelete(row)}
        className="px-2 py-1 rounded-lg bg-rose-600 hover:bg-rose-700 text-white"
        title="Supprimer"
      >
        <FaTrash />
      </button>
    </div>
  );
}

function ObjectivesTable({ rows, onEdit, onDelete, onToggleDone, onChangeProgress }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200">
            <tr className="uppercase tracking-wide text-xs">
              <th className="px-3 py-3 text-left font-semibold">Adhérent</th>
              <th className="px-3 py-3 text-left font-semibold">Groupe(s)</th>
              <th className="px-3 py-3 text-left font-semibold">Saison</th>
              <th className="px-3 py-3 text-left font-semibold">Titre</th>
              <th className="px-3 py-3 text-left font-semibold">Type</th>
              <th className="px-3 py-3 text-left font-semibold w-56">Progression</th>
              <th className="px-3 py-3 text-left font-semibold">Statut</th>
              <th className="px-3 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-slate-600 dark:text-slate-300">
                  Aucun objectif trouvé avec ces filtres.
                </td>
              </tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40">
                <td className="px-3 py-3">
                  <div className="font-medium">{(r.adherent_nom || "").toUpperCase()} {r.adherent_prenom || ""}</div>
                  <div className="text-[11px] opacity-70">#{r.adherent_id}</div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-xs">{r.group_names || "—"}</div>
                </td>
                <td className="px-3 py-3">{r.saison || "—"}</td>
                <td className="px-3 py-3">
                  <div className="font-medium">{r.title}</div>
                  {r.description && <div className="text-xs opacity-80 line-clamp-2">{r.description}</div>}
                </td>
                <td className="px-3 py-3">{r.type ? <Pill tone="emerald">{r.type}</Pill> : <Pill tone="slate">—</Pill>}</td>
                <td className="px-3 py-3">
                  <Progress value={r.progress} />
                  {!r.is_done && (
                    <input
                      type="range" min={0} max={100}
                      value={Number(r.progress) || 0}
                      onChange={(e) => onChangeProgress(r, Number(e.target.value))}
                      className="w-full mt-1"
                    />
                  )}
                </td>
                <td className="px-3 py-3">{r.is_done ? <Pill>Terminé</Pill> : <Pill tone="slate">En cours</Pill>}</td>
                <td className="px-3 py-3 text-right">
                  <RowActions row={r} onEdit={onEdit} onDelete={onDelete} onToggleDone={onToggleDone} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function ObjectifsAdmin() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const authorized = userRole === "admin" || userRole === "enseignant";

  // Data
  const [allGroups, setAllGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("");
  const [adherents, setAdherents] = useState([]);

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");

  // Objectif filters
  const [typeFilter, setTypeFilter] = useState("");
  const [isDoneFilter, setIsDoneFilter] = useState("");
  const [search, setSearch] = useState("");

  // UI
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalAdherentId, setModalAdherentId] = useState(null);

  // Init
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [settings, adherentList, groupList] = await Promise.all([
          getSettings(),
          getAllAdherentsListWithGroup(),
          getGroupsList(),
        ]);

        if (Array.isArray(settings) && settings[0]?.saison_active) {
          const active = settings[0].saison_active;
          const { prev, next } = seasonNeighbors(active);
          setSeasons([prev, active, next]);
          setSelectedSeason(active);
        } else {
          const active = seasonLabel();
          const { prev, next } = seasonNeighbors(active);
          setSeasons([prev, active, next]);
          setSelectedSeason(active);
        }

        setAllGroups(Array.isArray(groupList) ? groupList : []);
        setAdherents(Array.isArray(adherentList) ? adherentList : []);
      } catch (e) {
        console.error(e);
        toast.error("Erreur de chargement des données.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);

      const filteredAdh = (adherents || []).filter((a) => inGroup(a, filterGroup));
      const ids = (filteredAdh.length ? filteredAdh : adherents).map((a) => a.id);

      if (!ids.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      const params = {
        saison: selectedSeason || undefined,              // ⚠ saison d'Objectif
        type: typeFilter || undefined,
        is_done: isDoneFilter === "" ? undefined : isDoneFilter === "true",
      };

      const CHUNK = 25;
      let all = [];
      for (let i = 0; i < ids.length; i += CHUNK) {
        const batch = ids.slice(i, i + CHUNK);
        const part = await Promise.all(
          batch.map((id) => getObjectifsByAdherent(id, params).catch(() => []))
        );
        all = all.concat(...part);
      }

      const adhIdx = new Map(adherents.map((a) => [a.id, a]));
      let mapped = all.map((o) => {
        const a = adhIdx.get(o.adherent_id);
        const groupsTxt = (a?.Groups || []).map((gr) => gr.nom).join(", ");
        return {
          ...o,
          adherent_nom: a?.nom || "",
          adherent_prenom: a?.prenom || "",
          group_names: groupsTxt || null,
        };
      });

      // Filtres locaux (sécurité)
      mapped = mapped.filter((r) => (
        (!selectedSeason || r.saison === selectedSeason) &&
        (!typeFilter || r.type === typeFilter) &&
        (isDoneFilter === "" || r.is_done === (isDoneFilter === "true"))
      ));

      // Recherche locale
      const q = search.trim().toLowerCase();
      if (q) {
        mapped = mapped.filter((r) => {
          const hay = [r.title || "", r.description || "", r.adherent_nom || "", r.adherent_prenom || ""]
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        });
      }

      mapped.sort((x, y) => new Date(y.createdAt || y.id || 0) - new Date(x.createdAt || x.id || 0));
      setRows(mapped);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des objectifs.");
    } finally {
      setLoading(false);
    }
  }, [adherents, filterGroup, isDoneFilter, search, selectedSeason, typeFilter]);

  // 1) première charge
  useEffect(() => {
    if (!adherents.length) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adherents, selectedSeason]);

  // 2) live sur filtres (groupe/type/statut/saison)
  useEffect(() => {
    if (!adherents.length) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGroup, typeFilter, isDoneFilter, selectedSeason]);

  // 3) debounce recherche
  const searchRef = useRef();
  useEffect(() => {
    if (!adherents.length) return;
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => refresh(), 400);
    return () => clearTimeout(searchRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /* ---------- Actions lignes ---------- */
  const openCreateFor = (adherentId) => {
    setEditing(null);
    setModalAdherentId(adherentId || null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setModalAdherentId(row.adherent_id);
    setModalOpen(true);
  };

  const handleSubmitModal = async (payload, idToUpdate) => {
    try {
      if (!payload.adherent_id) payload.adherent_id = Number(modalAdherentId);
      if (!payload.adherent_id) return toast.info("Sélectionnez un adhérent pour créer l’objectif.");
      if (idToUpdate) {
        await updateObjectif(idToUpdate, payload);
        toast.success("Objectif mis à jour.");
      } else {
        await createObjectif(payload);
        toast.success("Objectif créé.");
      }
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error("Échec de l’enregistrement.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Supprimer l’objectif « ${row.title} » de ${row.adherent_nom} ${row.adherent_prenom} ?`)) return;
    try {
      await deleteObjectif(row.id);
      toast.success("Objectif supprimé.");
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error("Impossible de supprimer cet objectif.");
    }
  };

  const handleToggleDone = async (row) => {
    try {
      await setObjectifDone(row.id, !row.is_done);
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error("Impossible de changer l’état.");
    }
  };

  const handleChangeProgress = async (row, value) => {
    try {
      await setObjectifProgress(row.id, value);
      setRows((r) =>
        r.map((x) =>
          x.id === row.id ? { ...x, progress: value, is_done: value >= 100 ? true : x.is_done } : x
        )
      );
    } catch (e) {
      console.error(e);
      toast.error("Échec de la mise à jour de la progression.");
    }
  };

  // Liste d’adhérents pour le select "Nouvel objectif"
  const adherentsForSelect = useMemo(() => {
    return (adherents || [])
      .filter((a) => inGroup(a, filterGroup))
      .sort((a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" }) ||
        (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" })
      );
  }, [adherents, filterGroup]);

  /* ---------- Rendu ---------- */
  return (
    <section className="w-full">
      <ScrollIndicator />
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle role="Enseignant" title="Objectifs de saison — vue enseignant" align="center" className="mb-1" />
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {!authorized ? (
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur p-8 text-center text-slate-700 dark:text-slate-200">
            Accès réservé aux enseignants / administrateurs.
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
            {/* Bandeau dégradé */}
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 sm:px-8 py-6 text-white">
              <h1 className="text-lg sm:text-xl font-semibold text-center">Gestion des objectifs par groupes</h1>
              <p className="text-sm/6 opacity-95 text-center mt-1">
                Filtrez par groupe / saison, puis par type, statut, et recherche plein texte.
                <br />Créez et éditez des objectifs pour les adhérents filtrés.
              </p>
            </div>

            {/* Filtres */}
            <div className="px-5 sm:px-8 py-5">
              <FiltersBar
                loading={loading}
                groups={allGroups}
                filterGroup={filterGroup}
                onGroupChange={setFilterGroup}
                seasons={seasons}
                selectedSeason={selectedSeason}
                onSeasonChange={setSelectedSeason}
                typeFilter={typeFilter}
                onTypeChange={setTypeFilter}
                isDoneFilter={isDoneFilter}
                onIsDoneChange={setIsDoneFilter}
                search={search}
                onSearchChange={setSearch}
              />
            </div>

            {/* Toolbar + création */}
            <div className="px-5 sm:px-8 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="text-sm text-slate-700 dark:text-slate-300">
                {loading ? "Chargement…" : `${rows.length} objectif${rows.length > 1 ? "s" : ""}`}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={modalAdherentId || ""}
                  onChange={(e) => setModalAdherentId(e.target.value)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                >
                  <option value="">{filterGroup ? "Choisir adhérent filtré…" : "Choisir adhérent…"}</option>
                  {adherentsForSelect.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.nom || "").toUpperCase()} {a.prenom}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => openCreateFor(modalAdherentId)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-white bg-violet-600 hover:bg-violet-700"
                >
                  <FaPlus /> Nouvel objectif
                </button>
              </div>
            </div>

            {/* Tableau */}
            <div className="px-5 sm:px-8 pb-6">
              {loading ? (
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-8 text-center text-slate-600 dark:text-slate-300">
                  Chargement…
                </div>
              ) : (
                <ObjectivesTable
                  rows={rows}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onToggleDone={handleToggleDone}
                  onChangeProgress={handleChangeProgress}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modale create/edit */}
      <CreateEditObjectifModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSubmit={handleSubmitModal}
        initial={editing}
        adherentId={modalAdherentId ? Number(modalAdherentId) : null}
      />

      <ToastContainer />
    </section>
  );
}
